














































import { defineComponent, reactive, toRefs, PropType, computed, onMounted, onBeforeUnmount } from '@vue/composition-api';
import * as Common from "../commons/common"
// @ts-ignore
import VueDragResize from "vue-drag-resize";
// @ts-ignore
import { longClickDirective } from 'vue-long-click'
const longClickInstance = longClickDirective({delay: 600, interval: 0})

export interface Data {
  margin: number
  isDrawing: boolean
  startPos: Common.Point
  endPos: Common.Point
  scrollPos: Common.Point
  textStampIdx: number
  isTouching: boolean
  isDragging: boolean
  isResizing: boolean
  isScrolling: boolean
  dragMinRange: number
  drawModeCache: number
  stickSize: number
  aspectRatio: boolean
}

export default defineComponent({
  directives: {
    longclick: longClickInstance
  },
  props: {
    id: {
      type: Number as PropType<number>,
      required: false,
    },
    base64Canvas: {
      type: String as PropType<string>,
      required: false,
      default: null,
    },
    textStamps: {
      type: Array as PropType<any[]>,
      required: false,
      default: []
    },
    imageStamps: {
      type: Array as PropType<any[]>,
      required: false,
      default: []
    },
    scoreStamps: {
      type: Array as PropType<any[]>,
      required: false,
      default: []
    },
    isDrawable: {
      type: Boolean as PropType<boolean>,
      required: true
    },
    selectedStamp: {
      type: Object || null as PropType<any>,
      required: false,
      default: null,
    },
    scale: {
      type: Number as PropType<number>,
      required: true
    },
    drawMode: {
      type: Number as PropType<number>,
      required: true
    },
    fontSize: {
      type: Number as PropType<number>,
      required: true
    },
    lineHeight: {
      type: Number as PropType<number>,
      required: true
    },
    keyCode: {
      type: String as PropType<string>,
      required: false,
      default: null
    },
    touchLength: {
      type: Number as PropType<number>,
      required: true
    },
    idPrefix: {
      type: String as PropType<string>,
      required: true,
    },
    areaWidth: {
      type: Number as PropType<number>,
      required: true
    },
    areaHeight: {
      type: Number as PropType<number>,
      required: true
    },
    posX: {
      type: Number as PropType<number>,
      required: true
    },
    posY: {
      type: Number as PropType<number>,
      required: true
    },
    thickness: {
      type: Number as PropType<number>,
      required: true
    },
    drawColor: {
      type: String as PropType<string>,
      required: true
    },
    boxShadowStyle: {
      type: String as PropType<string>,
      required: false,
      default: null,
    },
    isLongClickEnable: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    isMarkerModeEnable: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
  },
  components: {
    "vue-drag-resize": VueDragResize,
  },
  setup (props, context) {

    const DrawModeEnum = Common.DrawModeEnum;

    const state = reactive<Data>({
      margin: 50,
      isDrawing: false,
      startPos: new Common.Point(0, 0),
      endPos: new Common.Point(0, 0),
      scrollPos: new Common.Point(0, 0),
      textStampIdx: 0,
      isTouching: false,
      isDragging: false,
      isResizing: false,
      isScrolling: false,
      dragMinRange: 5,
      drawModeCache: null,
      stickSize: 8,
      aspectRatio: false,
    });

    /************************************************************
     * 画像の親DivのCSSスタイル
     ************************************************************/

    const imageWrapStyle = computed((): any => {
      const style: any = {};
      style["position"] = "absolute";
      style["width"] = `${props.areaWidth * props.scale}px`;
      style["height"] = `${props.areaHeight * props.scale}px`;
      style["left"] = `${props.posX * props.scale}px`;
      style["top"] = `${props.posY * props.scale}px`;

      if(props.isDrawable){
        style["box-shadow"] = props.boxShadowStyle ? props.boxShadowStyle : "0 0 4px 4px blue";
        style["z-index"] = "4";
      }else{
        style["box-shadow"] = "";
        style["z-index"] = "1";
      }
      return style;
    });

    /************************************************************
     * canvasのCSSスタイル
     ************************************************************/

    const canvasStyle = computed((): any => {
      const style: any = {};
      style["width"] = `${props.areaWidth * props.scale}px`;
      style["height"] = `${props.areaHeight * props.scale}px`;
      style["z-index"] = isCanvasMode.value ? 2 : 1;
      return style;
    });

    /************************************************************
     * テキストスタンプのCSSスタイル
     ************************************************************/

    const stampStyle = computed((): any => {
      const style: any = {};
      style["z-index"] = isCanvasMode.value ? 1 : 2;
      return style;
    });

    /************************************************************
     * 現在のモードがcanvas系か判定
     ************************************************************/

    const isCanvasMode = computed(() => {
      return [DrawModeEnum.FreeLine, DrawModeEnum.StraightLine, DrawModeEnum.Eraser, DrawModeEnum.Marker, DrawModeEnum.BracesStart, DrawModeEnum.BracesEnd].includes(props.drawMode);
    });

    /************************************************************
     * 現在のモードがスタンプ系か判定
     ************************************************************/

    const isStampMode = computed(() => {
      return [DrawModeEnum.Text, DrawModeEnum.ScoreStamp].includes(props.drawMode);
    });

    /************************************************************
     * マウス座標取得
     ************************************************************/

    const getMousePos = (e: MouseEvent) => {
      const x: number = e.offsetX / props.scale;
      const y: number = e.offsetY / props.scale;
      return new Common.Point(x, y);
    };

    /************************************************************
     * Touchイベントの座標取得
     ************************************************************/

    const getTouchPos = (t: Touch, canvas: HTMLCanvasElement) => {
      const x: number = (t.clientX - canvas.getBoundingClientRect().left) / props.scale;
      const y: number = (t.clientY - canvas.getBoundingClientRect().top) / props.scale;
      return new Common.Point(x, y);
    }

    /************************************************************
     * strokeStyle設定
     ************************************************************/

    const getStrokeStyle = () => {
      const r = parseInt(props.drawColor.slice(1, 3), 16);
      const g = parseInt(props.drawColor.slice(3, 5), 16);
      const b = parseInt(props.drawColor.slice(5, 7), 16);

      let alpha = 0.8;
      if(props.drawMode == DrawModeEnum.Eraser){
        alpha = 1.0;
      }else if(props.drawMode == DrawModeEnum.Marker){
        alpha = 0.6;
      }

      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    /************************************************************
     * 線の種別による太さを取得
     ************************************************************/

    const getThickness = () => {
      return props.drawMode == DrawModeEnum.Marker || props.drawMode == DrawModeEnum.Eraser ? props.thickness * 8 : props.thickness;
    }

    /************************************************************
     * canvas MouseDown
     ************************************************************/

    const canvasMouseDown = (e: MouseEvent) => {

      const canvas = e.target as HTMLCanvasElement;
      state.startPos = getMousePos(e);

      if(isStampMode.value || ["t", "s"].includes(props.keyCode)){
        if(state.isDragging || state.isResizing){
          state.isDragging = false;
          state.isResizing = false;
        }else if(props.drawMode == DrawModeEnum.Text || props.keyCode == "t"){
          const param = {point: state.startPos};
          context.emit("add-text-stamp", param);
          context.emit("change-draw-mode-from-child", DrawModeEnum.None);
        }else if(props.drawMode == DrawModeEnum.ScoreStamp || props.keyCode == "s"){
          const param = {point: state.startPos};
          context.emit("add-score-stamp", param);
        }else{
          context.emit("change-selected-stamp", null);
        }
      }else if(isCanvasMode.value || ["e", "d", "m"].includes(props.keyCode)){

        if(!props.isMarkerModeEnable && (props.drawMode == DrawModeEnum.Marker || props.keyCode == "m")){
          return;
        }

        if(props.keyCode == "d"){
          state.drawModeCache = props.drawMode;
          context.emit("change-draw-mode-from-child", DrawModeEnum.FreeLine);
        }else if(props.keyCode == "e"){
          state.drawModeCache = props.drawMode;
          context.emit("change-draw-mode-from-child", DrawModeEnum.Eraser);
        }else if(props.keyCode == "m"){
          state.drawModeCache = props.drawMode;
          context.emit("change-draw-mode-from-child", DrawModeEnum.Marker);
        }

        e.preventDefault();
        state.isDrawing = true;
        const ctx = canvas.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D;
        ctx.strokeStyle = getStrokeStyle();

        context.root.$nextTick(() => {
          if(props.drawMode == DrawModeEnum.BracesStart || props.drawMode == DrawModeEnum.BracesEnd){
            drawBrace(ctx);
            drawEnd(canvas, ctx);
          }
        });
      }else{
        context.emit("change-selected-stamp", null);
      }
    };

    /************************************************************
     * canvas MouseMove
     ************************************************************/

    const canvasMouseMove = (e: MouseEvent) => {

      if(!state.isDrawing || (props.drawMode != DrawModeEnum.FreeLine && props.drawMode != DrawModeEnum.Eraser && props.drawMode != DrawModeEnum.Marker)){
        return;
      }

      state.endPos = getMousePos(e);
      const ctx = (e.target as HTMLCanvasElement).getContext("2d");
      drawLine(ctx);
      state.startPos = state.endPos;
    };

    /************************************************************
     * canvas MouseUp
     ************************************************************/

    const canvasMouseUp = (e: MouseEvent) => {

      if(!state.isDrawing){
        return;
      }

      const canvas = e.target as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");

      if(props.drawMode == DrawModeEnum.Eraser){
        ctx.globalCompositeOperation = "source-over";
      }else if(props.drawMode == DrawModeEnum.StraightLine){
        state.endPos = getMousePos(e);
        const lineCtx = canvas.getContext("2d");
        drawLine(lineCtx);
      }
      drawEnd(canvas, ctx);

      if(state.drawModeCache != null){
        context.emit("change-draw-mode-from-child", state.drawModeCache);
        state.drawModeCache = null;
      }
    };

    /************************************************************
     * canvas MouseLeave
     ************************************************************/

    const canvasMouseLeave = (e: MouseEvent) => {
      if(state.isDrawing){
        const canvas = e.target as HTMLCanvasElement;
        const ctx = canvas.getContext("2d");
        drawEnd(canvas, ctx);

        if(state.drawModeCache != null){
          context.emit("change-draw-mode-from-child", state.drawModeCache);
          state.drawModeCache = null;
        }
      }
    };

    /************************************************************
     * canvas TouchStart
     ************************************************************/

    const canvasTouchStart = (e: TouchEvent) => {

      const canvas = (e as TouchEvent).target as HTMLCanvasElement;
      state.startPos = getTouchPos(e.changedTouches[0], canvas);
      state.scrollPos = new Common.Point(canvas.scrollLeft, canvas.scrollTop);

      if(isCanvasMode.value){
        e.preventDefault();
        state.isDrawing = true;
        const ctx = canvas.getContext("2d");
        ctx.strokeStyle = getStrokeStyle();

        context.root.$nextTick(() => {
          if(props.drawMode == DrawModeEnum.BracesStart || props.drawMode == DrawModeEnum.BracesEnd){
            drawBrace(ctx);
            drawEnd(canvas, ctx);
          }
        });
      }else if(state.isDragging || state.isResizing){
        state.isDragging = false;
        state.isResizing = false;
      }else if(props.drawMode == DrawModeEnum.Text && !isMultiTouching.value){
        const param = {point: state.startPos};
        context.emit("add-text-stamp", param);
        context.emit("change-draw-mode-from-child", DrawModeEnum.None);
      }else{
        context.emit("change-selected-stamp", null);
      }
    };

    /************************************************************
     * canvas TouchMove
     ************************************************************/

    const canvasTouchMove = (e: TouchEvent) => {

      if(!state.isDrawing){
        const canvas: HTMLCanvasElement = document.getElementById(`${props.idPrefix}-canvas`) as HTMLCanvasElement;
        const endPos = getTouchPos((e as TouchEvent).changedTouches[0], canvas);
        const scrollEndPos = new Common.Point(canvas.scrollLeft, canvas.scrollTop);
        const limit = 5;
        if(endPos.x - state.startPos.x > limit || endPos.x - state.startPos.x < -limit || endPos.y - state.startPos.y > limit || endPos.y - state.startPos.y < -limit
          || scrollEndPos.x - state.scrollPos.x >  limit || scrollEndPos.x - state.scrollPos.x < -limit || scrollEndPos.y - state.scrollPos.y >  limit || scrollEndPos.y - state.scrollPos.y < -limit){
          state.isScrolling = true;
        }
      }else{
        e.preventDefault();

        if(props.drawMode == DrawModeEnum.FreeLine || props.drawMode == DrawModeEnum.Eraser || props.drawMode == DrawModeEnum.Marker){
          const canvas = e.target as HTMLCanvasElement;
          state.endPos = getTouchPos(e.changedTouches[0], canvas);
          const ctx = canvas.getContext("2d");
          drawLine(ctx);
          state.startPos = state.endPos;
        }
      }
    };

    /************************************************************
     * canvas TouchEnd
     ************************************************************/

    const canvasTouchEnd = (e: TouchEvent) => {

      state.isScrolling = false;

      if(!state.isDrawing){
        return;
      }

      e.preventDefault();

      const canvas = e.target as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");

      if(props.drawMode == DrawModeEnum.StraightLine){
        state.endPos = getTouchPos(e.changedTouches[0], canvas);
        const lineCtx = canvas.getContext("2d");
        drawLine(lineCtx);
      }

      drawEnd(canvas, ctx);

      if(state.drawModeCache != null){
        context.emit("change-draw-mode-from-child", state.drawModeCache);
        state.drawModeCache = null;
      }
    };

    /************************************************************
     * canvas 自由線・消しゴム描画
     ************************************************************/

    const drawLine = (ctx: CanvasRenderingContext2D) => {
      if(props.drawMode == DrawModeEnum.Eraser){
        ctx.globalCompositeOperation = "destination-out";
        ctx.lineWidth = getThickness();
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.globalAlpha = 1.0;
        ctx.beginPath();
        ctx.moveTo(state.startPos.x, state.startPos.y);
        ctx.lineTo(state.endPos.x, state.endPos.y);
        ctx.stroke();
      }else {
        ctx.globalCompositeOperation = "source-over";
        ctx.lineWidth = getThickness();
        ctx.lineJoin = props.drawMode == DrawModeEnum.Marker ? 'miter' : 'round';
        ctx.lineCap = props.drawMode == DrawModeEnum.Marker ? 'butt' : 'round';
        ctx.beginPath();
        ctx.moveTo(state.startPos.x, state.startPos.y);
        ctx.lineTo(state.endPos.x, state.endPos.y);
        ctx.stroke();
      }
    };

    /************************************************************
     * canvas カッコ描画
     ************************************************************/

    const drawBrace = (ctx: CanvasRenderingContext2D) => {
      const braceSize = 30;
      ctx.lineWidth = props.thickness;

      ctx.beginPath();

      if(props.drawMode == DrawModeEnum.BracesStart){
        ctx.moveTo(state.startPos.x + braceSize, state.startPos.y);
        ctx.lineTo(state.startPos.x, state.startPos.y);
        ctx.stroke();
        ctx.moveTo(state.startPos.x, state.startPos.y);
        ctx.lineTo(state.startPos.x, state.startPos.y + braceSize);
        ctx.stroke();
      }else{
        ctx.moveTo(state.startPos.x, state.startPos.y - braceSize);
        ctx.lineTo(state.startPos.x, state.startPos.y);
        ctx.stroke();
        ctx.moveTo(state.startPos.x, state.startPos.y);
        ctx.lineTo(state.startPos.x - braceSize, state.startPos.y);
        ctx.stroke();
      }
    }

    /************************************************************
     * canvas 編集完了後処理
     ************************************************************/

    const drawEnd = (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => {
      state.isDrawing = false;
      const base64Canvas = canvas.toDataURL("image/png").replace("data:image/png;base64,", "");
      const bitCnt = ctx.getImageData(0, 0, props.areaWidth, props.areaHeight).data.filter(x => x > 0).length;
      context.emit("update-canvas", base64Canvas, bitCnt);
    }

    /************************************************************
     * テキスト画像ドラッグ中
     ************************************************************/

    const textDragging = (textStamp: any, rect: any) => {
      const movedX = rect.left - textStamp.x * props.scale;
      const movedY = rect.top - textStamp.y * props.scale;
      state.isDragging = movedX > state.dragMinRange || movedX < -state.dragMinRange || movedY > state.dragMinRange || movedY < -state.dragMinRange;
    }

    /************************************************************
     * テキスト画像ドラッグ終了
     ************************************************************/

    const textDragStop = (textStamp: any, rect: any) => {

      if(rect.left < 0){
        textStamp.x = 0;
      }else if(rect.left + rect.width > props.areaWidth * props.scale){
        textStamp.x = (props.areaWidth * props.scale - rect.width) / props.scale;
      }else{
        textStamp.x = rect.left / props.scale;
      }

      if(rect.top < 0){
        textStamp.y = 0;
      }else if(rect.top + rect.height > props.areaHeight * props.scale){
        textStamp.y = (props.areaHeight * props.scale - rect.height) / props.scale;
      }else{
        textStamp.y = rect.top / props.scale;
      }
      textStamp.x = rect.left / props.scale;
      textStamp.y = rect.top / props.scale;

      context.emit("text-stamp-dragged", textStamp);
    }

    /************************************************************
     * AnswerTextスタンプ mousedown
     ************************************************************/

    const textMouseDown = () => {
      state.isTouching = true;
    }

    /************************************************************
     * AnswerTextスタンプ mouseup
     ************************************************************/

    const textMouseUp = (textStamp: any) => {
      state.isTouching = false;
      if(!props.textStamps.includes(textStamp)){
        return;
      }

      if(props.drawMode == DrawModeEnum.Delete && !isMultiTouching.value){
        context.emit("delete-text-stamp", textStamp);
      }else{
        context.emit("change-selected-stamp", textStamp);
      }
    }

    /************************************************************
     * 画像上を長押しテキスト出力
     ************************************************************/

    const canvasLongClicked = () => {
      if(props.isLongClickEnable && props.drawMode == DrawModeEnum.None && !state.isScrolling && !isMultiTouching.value){
        const param = {point: state.startPos};
        context.emit("add-text-stamp", param);
        context.emit("change-draw-mode-from-child", DrawModeEnum.None);
      }
    }

    /************************************************************
     * テキスト長押し削除
     ************************************************************/

    const textLongClicked = (textStamp: any) => {
      if(props.isLongClickEnable && state.isTouching && !state.isDragging && props.drawMode != DrawModeEnum.Delete && !isMultiTouching.value){
        context.emit("delete-text-stamp", textStamp);
      }
    }

    /************************************************************
     * 画像スタンプ長押し削除
     ************************************************************/

    const imageStampLongClicked = (imageStamp: any) => {
      if(props.isLongClickEnable && state.isTouching && !state.isDragging && props.drawMode != DrawModeEnum.Delete && !isMultiTouching.value){
        context.emit("delete-image-stamp", imageStamp);
      }
    }

    /************************************************************
     * 部分点スタンプ長押し削除
     ************************************************************/

    const scoreStampLongClicked = (scoreStamp: any) => {
      if(props.isLongClickEnable && state.isTouching && !state.isDragging && props.drawMode != DrawModeEnum.Delete && !isMultiTouching.value){
        context.emit("delete-score-stamp", scoreStamp);
      }
    }

    /************************************************************
     * マルチタップ中か判定
     * 2本以上の指が検出されている、または1本はテキスト、それ以外に指が検出されている場合にtrue
     * テキストタッチはtouchLengthに加算されない場合があるのでisTouchingで判定する
     ************************************************************/

    const isMultiTouching = computed((): any => {
      return props.touchLength >= 2 || (props.touchLength == 1 && state.isTouching);
    });

    /************************************************************
     * スタンプ mousedown
     ************************************************************/

    const imageStampMouseDown = () => {
      state.isTouching = true;
      state.aspectRatio = true;
    }

    /************************************************************
     * 画像スタンプ mouseup
     ************************************************************/

    const imageStampMouseUp = (imageStamp: any) => {
      state.isTouching = false;

      if(!props.imageStamps.includes(imageStamp)){
        return;
      }

      if(props.drawMode == DrawModeEnum.Delete && !isMultiTouching.value){
        context.emit("delete-image-stamp", imageStamp);
      }else{
        context.emit("change-selected-stamp", imageStamp);
      }
    }

    /************************************************************
     * 画像スタンプクリック
     ************************************************************/

    const imageStampClick = (imageStamp: any, e: any) => {
      e.stopPropagation();
      context.emit("change-selected-stamp", imageStamp);
    }

    /************************************************************
     * 画像スタンプドラッグ中
     ************************************************************/

    const imageStampDragging = (imageStamp: any, rect: any) => {
      const movedX = rect.left - imageStamp.x * props.scale;
      const movedY = rect.top - imageStamp.y * props.scale;
      state.isDragging = movedX > state.dragMinRange || movedX < -state.dragMinRange || movedY > state.dragMinRange || movedY < -state.dragMinRange;
    }

    /************************************************************
     * 画像スタンプドラッグ終了
     ************************************************************/

    const imageStampDragStop = (imageStamp: any, rect: any) => {
      imageStamp.x = rect.left / props.scale;
      imageStamp.y = rect.top / props.scale;
      state.isDragging = false;
    }

    /****************************************
     * 画像スタンプリサイズ中
     ****************************************/

    const imageStampResizing = (imageStamp: any, rect: any) => {
      imageStamp.x = rect.left / props.scale;
      imageStamp.y = rect.top / props.scale;
      imageStamp.width = rect.width / props.scale;
      imageStamp.height = rect.height / props.scale;
      state.isResizing = true;
    };

    /****************************************
     * 画像スタンプリサイズ終了
     ****************************************/

    const imageStampResizeStop = (imageStamp: any, rect: any) => {
      context.root.$nextTick(() => {
        imageStamp.x = rect.left / props.scale;
        imageStamp.y = rect.top / props.scale;
        imageStamp.width = rect.width / props.scale;
        imageStamp.height = rect.height / props.scale;
        state.isResizing = false;
      });
    };

    /************************************************************
     * 部分点スタンプ mouseup
     ************************************************************/

    const scoreStampMouseUp = (scoreStamp: any) => {
      state.isTouching = false;

      if(!props.scoreStamps.includes(scoreStamp)){
        return;
      }

      if(props.drawMode == DrawModeEnum.Delete && !isMultiTouching.value){
        context.emit("delete-score-stamp", scoreStamp);
      }else{
        context.emit("change-selected-stamp", scoreStamp);
      }
    }

    /************************************************************
     * 部分点スタンプクリック
     ************************************************************/

    const scoreStampClick = (scoreStamp: any, e: any) => {
      e.stopPropagation();
      context.emit("change-selected-stamp", scoreStamp);
    }

    /************************************************************
     * 部分点スタンプドラッグ中
     ************************************************************/

    const scoreStampDragging = (scoreStamp: any, rect: any) => {
      const movedX = rect.left - scoreStamp.x * props.scale;
      const movedY = rect.top - scoreStamp.y * props.scale;
      state.isDragging = movedX > state.dragMinRange || movedX < -state.dragMinRange || movedY > state.dragMinRange || movedY < -state.dragMinRange;
    }

    /************************************************************
     * 部分点スタンプドラッグ終了
     ************************************************************/

    const scoreStampDragStop = (scoreStamp: any, rect: any) => {
      adjustScoreStampPositionByDragEnd(scoreStamp, rect);
      state.isDragging = false;
      context.emit("score-stamp-dragged", scoreStamp);
    }

    /************************************************************
     * 部分点スタンプドラッグ範囲チェック
     ************************************************************/

    const adjustScoreStampPositionByDragEnd = (scoreStamp: any, rect: any) => {
      if(rect.left < 0){
        scoreStamp.x = scoreStamp.x == 1 ? 0 : 1;
      }else if(rect.left + rect.width > props.areaWidth * props.scale){
        const limitX = (props.areaWidth * props.scale - rect.width) / props.scale;
        scoreStamp.x = scoreStamp.x == limitX ? limitX - 1 : limitX;
      }else{
        scoreStamp.x = rect.left / props.scale;
      }

      if(rect.top < 0){
        scoreStamp.y = scoreStamp.y == 1 ? 0 : 1;
      }else if(rect.top + rect.height > props.areaHeight * props.scale){
        const limitY = (props.areaHeight * props.scale - rect.height) / props.scale;
        scoreStamp.y = scoreStamp.y == limitY ? limitY - 1 : limitY;
      }else{
        scoreStamp.y = rect.top / props.scale;
      }
    }

    /****************************************
     * 部分点スタンプリサイズ中
     ****************************************/

    const scoreStampResizing = (scoreStamp: any, rect: any) => {
      scoreStamp.x = rect.left / props.scale;
      scoreStamp.y = rect.top / props.scale;
      scoreStamp.width = rect.width / props.scale;
      scoreStamp.height = rect.height / props.scale;
      state.isResizing = true;
    };

    /****************************************
     * 部分点スタンプリサイズ終了
     ****************************************/

    const scoreStampResizeStop = (scoreStamp: any, rect: any) => {
      context.root.$nextTick(() => {
        adjustScoreStampPositionByResizeEnd(scoreStamp, rect);
        state.isResizing = false;
        context.emit("score-stamp-dragged", scoreStamp);
      });
    };

    /****************************************
     * 部分点スタンプリサイズ範囲チェック
     ****************************************/

    const adjustScoreStampPositionByResizeEnd = (scoreStamp: any, rect: any) => {
      let x = rect.left / props.scale;
      let y = rect.top / props.scale;
      let width = rect.width / props.scale;
      let height = rect.height / props.scale;

      if(rect.left < 0){
        const resizedWidth = (rect.left + rect.width) / props.scale;
        width = scoreStamp.width == resizedWidth ? resizedWidth - 1 : resizedWidth;
        x = scoreStamp.x == 0 ? 1 : 0;
      }else if(rect.left + rect.width > props.areaWidth * props.scale){
        const resizedWidth = (props.areaWidth * props.scale - rect.left) / props.scale;
        width = scoreStamp.width == resizedWidth ? resizedWidth - 1 : resizedWidth;
      }

      if(rect.top < 0){
        const resizedHeight = (rect.top + rect.height) / props.scale;
        height = scoreStamp.height == resizedHeight ? resizedHeight - 1 : resizedHeight;
        y = scoreStamp.y == 0 ? 1 : 0;
      }else if(rect.top + rect.height > props.areaHeight * props.scale){
        const resizedHeight = (props.areaHeight * props.scale - rect.top) / props.scale;
        height = scoreStamp.height == resizedHeight ? resizedHeight - 1 : resizedHeight;
      }

      scoreStamp.width = width;
      scoreStamp.height = height;

      context.root.$nextTick(() => {
        scoreStamp.x = x;
        scoreStamp.y = y;
      });
    }

    /************************************************************
     * canvas描画
     * 縦・横表示切り替え時に親から呼ばれる
     ************************************************************/

    const renderCanvas = () => {
      const canvas = document.getElementById(`${props.idPrefix}-canvas`) as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");
      if(props.base64Canvas){
        const canvasImage = new Image();
        canvasImage.src = `data:image/png;base64,${props.base64Canvas}`;
        canvasImage.onload = () => {
          ctx.drawImage(canvasImage, 0, 0);
        };
      }else{
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    }

    /************************************************************
     * mounted
     ************************************************************/

    onMounted(async () => {
      const canvas = document.getElementById(`${props.idPrefix}-canvas`) as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");
      ctx.lineJoin = "round";
      ctx.lineCap = "round";

      state.stickSize = context.root.$store.state.isMobile ? 32 : 12;

      props.textStamps.forEach(x => {
        if(!x.base64_text){
          const size = Common.getTextSize({text: x.text, fontSize: x.font_size, lineHeight: x.line_height, direction: x.direction});
          x.width = size.width;
          x.height = size.height;
          x.base64_text = Common.createBase64Text(x);
        }
      });

      if(props.base64Canvas){
        const canvasImage = new Image();
        canvasImage.src = `data:image/png;base64,${props.base64Canvas}`;
        canvasImage.onload = () => {
          ctx.drawImage(canvasImage, 0, 0);
          const bitCnt = ctx.getImageData(0, 0, props.areaWidth, props.areaHeight).data.filter(x => x > 0).length;
          if(props.id != null){
            context.emit("set-is-canvas-drawed-from-child", props.id, bitCnt);
          }
        };
      }
    })

    /************************************************************
     * before unmount
     ************************************************************/

    onBeforeUnmount(async () => {
      const canvas = document.getElementById(`${props.idPrefix}-canvas`) as HTMLCanvasElement;
      if(canvas != null){
        canvas.width = 0;
        canvas.height = 0;
        canvas.remove();
      }
    });

    return {
      ...toRefs(state),
      canvasStyle,
      stampStyle,
      imageWrapStyle,
      canvasMouseDown,
      canvasMouseMove,
      canvasMouseUp,
      canvasMouseLeave,
      canvasTouchStart,
      canvasTouchMove,
      canvasTouchEnd,
      textDragging,
      textDragStop,
      textMouseDown,
      textMouseUp,
      textLongClicked,
      canvasLongClicked,
      renderCanvas,
      imageStampClick,
      imageStampDragging,
      imageStampDragStop,
      imageStampResizing,
      imageStampResizeStop,
      imageStampLongClicked,
      imageStampMouseDown,
      imageStampMouseUp,
      scoreStampClick,
      scoreStampMouseUp,
      scoreStampDragging,
      scoreStampDragStop,
      scoreStampResizing,
      scoreStampResizeStop,
      scoreStampLongClicked,
    }
  }
});
